import React, { useCallback, useState, useEffect, useRef } from 'react';
import { CircularProgress } from './CircularProgress';
import { Play, Pause, SkipForward, RotateCcw } from 'lucide-react';
import { useTimer } from '../../context/TimerContext';
import { audioManager } from '../../utils/audio';

function formatTime(seconds: number): string {
  // Ensure input is a non-negative integer
  const validSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(validSeconds / 60);
  const remainingSeconds = validSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}

// Define props interface if not already defined, or add to existing one
interface TimerDisplayProps {
  isSettingsOpen: boolean;
}

export function TimerDisplay({ isSettingsOpen }: TimerDisplayProps) {
  // Use the specific actions needed from the context
  const { state, settings, startTimer, pauseTimer, skipStage, resetTimer, resetAllPomodoros, sessionLabel, setSessionLabel } = useTimer();
  const [isStarting, setIsStarting] = useState(false); // Keep local loading state for start button visual feedback
  const [confirmArmed, setConfirmArmed] = useState(false); // Two-step confirm for full cycle reset
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const confirmTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!confirmArmed) return;

    const dismissOnOutsideTap = (event: PointerEvent) => {
      if (!confirmButtonRef.current?.contains(event.target as Node)) {
        setConfirmArmed(false);
      }
    };
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setConfirmArmed(false);
    };

    document.addEventListener('pointerdown', dismissOnOutsideTap);
    document.addEventListener('keydown', dismissOnEscape);
    return () => {
      document.removeEventListener('pointerdown', dismissOnOutsideTap);
      document.removeEventListener('keydown', dismissOnEscape);
    };
  }, [confirmArmed]);

  useEffect(() => () => {
    if (confirmTimeoutRef.current) clearTimeout(confirmTimeoutRef.current);
  }, []);

  // Calculate duration based on the *current* stage in state
  const currentDurationKey = state.stage === 'focus'
    ? 'focusDuration'
    : state.stage === 'shortBreak'
      ? 'shortBreakDuration'
      : 'longBreakDuration';
  const currentDuration = settings[currentDurationKey];

  // Calculate progress based on timeRemaining relative to the full duration
  // Ensure currentDuration is not zero to prevent division by zero
  const progress = currentDuration > 0
    ? 1 - (state.timeRemaining / currentDuration)
    : 0; // Default to 0 progress if duration is 0

  const stageColors = {
    focus: 'text-indigo-600 dark:text-indigo-500',
    shortBreak: 'text-green-600 dark:text-green-500',
    longBreak: 'text-blue-600 dark:text-blue-500',
  };

  const dotColors = {
    completed: 'bg-indigo-600 dark:bg-indigo-400',
    active:
      'bg-indigo-600 dark:bg-indigo-400 ring-2 ring-indigo-300 dark:ring-indigo-500 ring-offset-2 ring-offset-gray-50 dark:ring-offset-gray-900',
    inactive: 'bg-gray-300 dark:bg-gray-600',
  };

  const buttonBgColors = {
      focus: 'bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600',
      shortBreak: 'bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600',
      longBreak: 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
  }

  const handleStartClick = useCallback(async () => {
    setIsStarting(true); // Indicate loading while audio potentially loads/plays

    // Call the context startTimer first to update state
    startTimer();

    // Play sound associated with the *current* stage being started/resumed
    if (settings.soundEnabled) {
      try {
        await audioManager.ensureInitialized(); // Ensure audio context is ready
        switch (state.stage) {
          case 'focus':
            await audioManager.playFocusSound(true); break;
          case 'shortBreak':
            await audioManager.playBreakSound(true); break;
          case 'longBreak':
            await audioManager.playLongBreakSound(true); break;
        }
      } catch (error) {
        console.warn('Audio playback on start failed:', error);
        // Optionally show a notification to the user
      }
    }
    setIsStarting(false); // Reset loading state
  }, [startTimer, settings.soundEnabled, state.stage]); // Add dependencies

  // Use pauseTimer directly from context
  const handlePauseClick = useCallback(() => {
    pauseTimer();
    setIsStarting(false);
  }, [pauseTimer]);

  // Use skipStage directly from context
  const handleSkipClick = useCallback(() => {
    skipStage();
  }, [skipStage]);

  // Use resetTimer directly from context
  const handleResetClick = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  // Determine button class based on stage
  const playPauseButtonClass = buttonBgColors[state.stage] || buttonBgColors.focus;

  // Effect for timer keyboard shortcuts (Space, Backspace, Enter)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // *** ADD THIS CHECK: If settings are open, do nothing for these keys ***
      if (isSettingsOpen) {
        return;
      }

      // Ignore if modifier keys are pressed (Ctrl, Alt, Shift, Meta)
      // (Keep this check as it's independent of settings panel state)
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }

      const target = event.target as HTMLElement;
      // Ignore if typing in form elements or contentEditable areas
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      // Handle timer key presses (only if settings are NOT open)
      switch (event.code) {
        case 'Space':
          event.preventDefault();
          if (!state.isRunning && isStarting) return;
          if (state.isRunning) handlePauseClick();
          else handleStartClick();
          break;
        case 'Backspace':
        case 'KeyR':
          event.preventDefault();
          handleResetClick();
          break;
        case 'Enter':
        case 'KeyS':
          event.preventDefault();
          handleSkipClick();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // Add isSettingsOpen to the dependency array
  }, [isSettingsOpen, state.isRunning, isStarting, handleStartClick, handlePauseClick, handleResetClick, handleSkipClick]);

  return (
    <div className="timer-display flex flex-col items-center justify-center space-y-8">
      <div className="timer-session relative">
        <input
          type="text"
          value={sessionLabel}
          onChange={(e) => setSessionLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !state.isRunning && !isStarting) {
              void handleStartClick();
            }
          }}
          placeholder="What are you working on?"
          maxLength={200}
          className="w-64 sm:w-72 text-center text-sm text-gray-600 dark:text-gray-400 bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-indigo-400 dark:focus:border-indigo-500 focus:outline-none transition-colors pb-1 placeholder:text-gray-400 dark:placeholder:text-gray-500"
          aria-label="What are you working on (recorded with focus sessions)"
        />
      </div>
      <div className="timer-progress relative">
        <CircularProgress
          progress={progress}
          // Use stageColors based on the current state.stage
          className={stageColors[state.stage] || stageColors.focus} // Default to focus color
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {/* Display timeRemaining directly from state */}
          <span className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white tabular-nums tracking-wider">
            {formatTime(state.timeRemaining)}
          </span>
          <span className="text-lg font-medium capitalize mt-2 text-gray-700 dark:text-gray-300">
            {/* Display current stage */}
            {state.stage === 'focus' ? 'Focus' : state.stage === 'shortBreak' ? 'Short Break' : 'Long Break'}
          </span>
        </div>
      </div>

      <div className="timer-controls flex items-center space-x-4">
        {/* Connect resetTimer */}
        <button
          onClick={handleResetClick}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-700 dark:text-gray-300"
          aria-label="Reset timer"
        >
          <RotateCcw className="w-6 h-6" />
        </button>

        {/* Connect startTimer / pauseTimer */}
        <button
          onClick={state.isRunning ? handlePauseClick : handleStartClick}
          // Disable button ONLY when it's the START button AND isStarting is true
          disabled={!state.isRunning && isStarting}
          className={`p-4 rounded-full text-white transition-colors shadow-md ${playPauseButtonClass} ${
              // Apply disabled style ONLY when it's the START button AND isStarting is true
              !state.isRunning && isStarting ? 'opacity-75 cursor-not-allowed' : ''
          }`}
          aria-label={state.isRunning ? "Pause timer" : "Start timer"}
        >
          {state.isRunning ? (
            <Pause className="w-8 h-8" />
          ) : (
            <Play className="w-8 h-8" />
          )}
        </button>

        {/* Connect skipStage */}
        <button
          onClick={handleSkipClick}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-700 dark:text-gray-300"
          aria-label="Skip to next stage"
        >
          <SkipForward className="w-6 h-6" />
        </button>
      </div>

      <p className="timer-shortcuts hidden sm:block text-[11px] text-gray-400 dark:text-gray-500 select-none">
        Space start/pause &middot; S skip &middot; R reset
      </p>

      {/* Pomodoro cycle indicator */}
      <div className="timer-cycles flex items-center space-x-2">
        {Array.from({ length: settings.cycleCount }).map((_, index) => (
          <div
            key={index}
            className={`w-3 h-3 rounded-full transition-colors ${
              index < state.completedPomodoros % settings.cycleCount
                ? dotColors.completed
                : state.stage === 'focus' &&
                    index === state.completedPomodoros % settings.cycleCount
                  ? dotColors.active
                  : dotColors.inactive
            }`}
             title={`Session ${index + 1}`}
          />
        ))}
      </div>
       <p className="timer-count text-sm text-gray-500 dark:text-gray-400">
            Pomodoro #{state.currentPomodoro}
       </p>

      {/* Keep the reset row reserved so revealing it never shifts the timer UI. */}
      <div className="timer-reset flex h-4 w-64 items-center justify-center">
        {(state.completedPomodoros > 0 || state.currentPomodoro > 1) && (
        <button
          ref={confirmButtonRef}
          onClick={() => {
            if (confirmArmed) {
              if (confirmTimeoutRef.current) clearTimeout(confirmTimeoutRef.current);
              setConfirmArmed(false);
              resetAllPomodoros();
            } else {
              setConfirmArmed(true);
              if (confirmTimeoutRef.current) clearTimeout(confirmTimeoutRef.current);
              confirmTimeoutRef.current = setTimeout(() => {
                setConfirmArmed(false);
                confirmTimeoutRef.current = null;
              }, 4000);
            }
          }}
          className={`w-64 whitespace-nowrap text-center text-xs transition-colors ${
            confirmArmed
              ? 'text-red-600 dark:text-red-400 font-medium'
              : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
          }`}
        >
          {confirmArmed ? 'Reset all pomodoros — sure?' : 'Start over'}
        </button>
        )}
      </div>
    </div>
  );
}
